import { TYPOGRAPHY } from '@app/constants';
import { FC } from 'react';
import { components, MultiValueProps, ValueContainerProps } from 'react-select';
import styled from 'styled-components';
import * as T from '../../types';
import Tag from '../Tag';

interface Props extends ValueContainerProps {
  className?: string;
}

const SelectedValuesContainer: FC<Props> = ({
  isDisabled,
  className,
  ...props
}) => {
  const { getOptionValue, value, onChange } = props.selectProps;

  const getValueLabel = (opt: T.Option) => opt.label;
  const getKey = (opt: T.Option, index: number) =>
    `${getOptionValue(opt)}-${index}`;

  const removeValue = (option: T.Option) => {
    const filteredValue = (value as T.Option[]).filter(
      (item) => item.label !== option.label,
    );
    onChange(filteredValue, { action: 'remove-value', removedValue: Option });
  };

  const makeMultiValue = (opt: T.Option, index: number) => (
    <components.MultiValue
      {...(props as MultiValueProps)}
      components={{
        Container: (props) => (
          <StyledContainer>
            <components.MultiValueContainer {...props} />
          </StyledContainer>
        ),
        Label: components.MultiValueLabel,
        Remove: components.MultiValueRemove,
      }}
      isDisabled={isDisabled}
      key={getKey(opt, index)}
      isFocused={false}
      index={index}
      removeProps={{
        onClick: () => removeValue(opt),
        onTouchEnd: () => removeValue(opt),
        onMouseDown: (e) => {
          e.preventDefault();
          e.stopPropagation();
        },
      }}
      data={opt}
      theme={{
        spacing: {
          baseUnit: 1,
          controlHeight: 1,
          menuGutter: 1,
        },
        borderRadius: 1,
        colors: {
          primary: 'lightgray',
          primary75: 'lightgray',
          primary50: 'lightgray',
          primary25: 'lightgray',
          danger: 'lightgray',
          dangerLight: 'lightgray',
          neutral0: 'lightgray',
          neutral5: 'lightgray',
          neutral10: 'lightgray',
          neutral20: 'lightgray',
          neutral30: 'lightgray',
          neutral40: 'lightgray',
          neutral50: 'lightgray',
          neutral60: 'lightgray',
          neutral70: 'lightgray',
          neutral80: 'lightgray',
          neutral90: 'lightgray',
        },
      }}
    >
      <Tag value={getValueLabel(opt)} onClick={() => removeValue(opt)} />
    </components.MultiValue>
  );

  return (
    <Root className={className}>
      {props
        .getValue()
        .map((item, index) => makeMultiValue(item as T.Option, index))}
    </Root>
  );
};

export default SelectedValuesContainer;

const Root = styled.div`
  ${TYPOGRAPHY.body1Regular14};
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  position: relative;
  overflow: hidden;
  -webkit-overflow-scrolling: touch;
  cursor: default;
`;

const StyledContainer = styled.div`
  & > div {
    background-color: unset;
    margin: 0;
    border-radius: 0;
  }

  & div[role='button'] {
    display: none;
  }
`;
